import { useState, useEffect, useMemo, useRef } from "react";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { useDashboardDomainContext } from "../DashboardDomainProvider";

export const useLayoutGenerator = (boundingBox) => {
  const { isViewMode, selectedPageVisuals, columnCount: ctxColumnCount } = useDashboardDomainContext();

  // ---- Responsive gates (tweak as needed) ---------------------------------
  const GATE_TO_5_COLUMNS = 900; // < 1200px → 5 columns
  const GATE_TO_2_COLUMNS = 700;  // <  900px → 2 columns
  const GATE_TO_1_COLUMN  = 400;  // <  600px → 1 column
  const MAX_DESKTOP_COLUMNS = ctxColumnCount; // default “wide” column plan
  // Local grid columns (avoid name clash with context "columnCount")
  const [gridColumns, setGridColumns] = useState(Number(MAX_DESKTOP_COLUMNS));

  useEffect(()=>{
    setGridColumns(Number(ctxColumnCount));
  },[ctxColumnCount]);

  const visuals = useMemo(
    () => selectedPageVisuals ?? [],
    [selectedPageVisuals]
  );

  // Dimensions and items
  const containerRef = useRef(null);
  const [rowHeight, setRowHeight] = useState(0);
  const [cards, setCards] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);

  // ---- Helpers -------------------------------------------------------------
  const debounce = (fn, ms = 150) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };

  const computeResponsiveColumns = (width) => {
    if (width < GATE_TO_1_COLUMN) return 1;
    if (width < GATE_TO_2_COLUMNS) return 2;
    if (width < GATE_TO_5_COLUMNS) return 5;
    return MAX_DESKTOP_COLUMNS;
  };

  // ---- Layout calculators --------------------------------------------------
  const updateDimensionsDynamic = (COLUMN_COUNT) => {
    const addNum = isViewMode ? 0 : 1;

    // resive all of them. we need 3 configs.. Gate1,2,3...
    if (!containerRef.current) return;
    const { width, height } = containerRef.current.getBoundingClientRect();

    // expose column count as a style variable for CSS calc()
    containerRef.current.style.setProperty("--column-count", String(gridColumns <= 4 ? 6 : gridColumns));

    // compute row height & visible rows from container size
    const newRowHeight = Math.floor(width / COLUMN_COUNT) || 1;
    const calcRows = Math.floor(height / newRowHeight) + addNum;

    // ---- helpers for packing ---------------------------------------------
    const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
    const getW = v => clamp((v?.Width ?? 1), 1, COLUMN_COUNT);
    const getH = v => Math.max(1, (v?.Height ?? 1));
    const getX = v => clamp((v?.PositionX ?? 0), 0, Math.max(0, COLUMN_COUNT - 1));
    const getY = v => Math.max(0, (v?.PositionY ?? 0));

    // occupancy grid: rows x COLUMN_COUNT; grows as needed
    let rowsUsed = 0;
    const grid = [];
    const ensureRows = (minRows) => {
      while (grid.length < minRows) {
        grid.push(new Array(COLUMN_COUNT).fill(false));
      }
    };
    const areaFree = (x, y, w, h) => {
      ensureRows(y + h);
      for (let r = y; r < y + h; r++) {
        for (let c = x; c < x + w; c++) {
          if (c < 0 || c >= COLUMN_COUNT) return false;
          if (grid[r][c]) return false;
        }
      }
      return true;
    };
    const stamp = (x, y, w, h) => {
      for (let r = y; r < y + h; r++) {
        for (let c = x; c < x + w; c++) grid[r][c] = true;
      }
      rowsUsed = Math.max(rowsUsed, y + h);
    };

    // Try to place a card, scanning rows downward; shrink width if overflow
    const placeCard = (prefX, prefY, w, h) => {
      if (prefX + w > COLUMN_COUNT) {
        w = clamp(w, 1, COLUMN_COUNT);
        prefX = clamp(prefX, 0, Math.max(0, COLUMN_COUNT - w));
      }
      let y = Math.max(0, prefY);
      const searchLimit = Math.max(rowsUsed + 50, calcRows + 50);
      for (; y < searchLimit; y++) {
        for (let x = prefX; x >= 0; x--) {
          if (x + w > COLUMN_COUNT) continue;
          if (areaFree(x, y, w, h)) return { x, y, w, h };
        }
        for (let x = 0; x <= COLUMN_COUNT - w; x++) {
          if (x === prefX) continue;
          if (areaFree(x, y, w, h)) return { x, y, w, h };
        }
      }
      return { x: 0, y: rowsUsed, w: clamp(w, 1, COLUMN_COUNT), h };
    };

    // normalize & stable order
    const normalized = (visuals ?? [])
      .map(v => ({
        ...v,
        _w: getW(v),
        _h: getH(v),
        _x: getX(v),
        _y: getY(v),
        _z: v?.ZIndex ?? 0
      }))
      .sort((a, b) => {
        if (a._y !== b._y) return a._y - b._y;
        if (a._x !== b._x) return a._x - b._x;
        return (a._z ?? 0) - (b._z ?? 0);
      });

    // pack without overlaps
    const packed = normalized.map(v => {
      const placed = placeCard(v._x, v._y, v._w, v._h);
      stamp(placed.x, placed.y, placed.w, placed.h);
      return {
        ...v,
        PositionX: placed.x,
        PositionY: placed.y,
        Width: placed.w,
        Height: placed.h
      };
    });

    const cardRows = Math.max(1, rowsUsed + addNum);
    const newRows = Math.max(calcRows, cardRows, 1);

    const [newCards, newPlaceholders] = generateLayout(packed, newRows, isViewMode, COLUMN_COUNT);

    setRowHeight(newRowHeight);
    setCards(newCards);
    setPlaceholders(newPlaceholders);
  };

  // ---- Install resize listener & initial layout ---------------------------
  useEffect(() => {
    const onResize = debounce(() => {
      if (!containerRef.current) return;
      const { width } = containerRef.current.getBoundingClientRect();

      const targetCols = computeResponsiveColumns(width);
      const currentCols = gridColumns;

      // Narrowing: drop columns to avoid overlap
      if (targetCols < currentCols) {
        setGridColumns(targetCols);
        updateDimensionsDynamic(targetCols);
        return;
      }
      // Widening: only update if it's greater than the current set column count
      if (targetCols > currentCols) {
        setGridColumns(targetCols);
        updateDimensionsDynamic(targetCols);
        return;
      }
      // Equal → just ensure layout is correct for current size
      updateDimensionsDynamic(currentCols);
    }, 150);

    // Initial pass
    onResize();

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // Re-run when visuals/isViewMode/gridColumns change
  }, [visuals, isViewMode, gridColumns]);

  const COLUMN_COUNT = gridColumns;

  const base = [...placeholders, ...cards];
  const layout = boundingBox ? [...base, boundingBox] : base;

  return {
    containerRef,
    layout,
    rowHeight,
    columnCount: COLUMN_COUNT,
  };
};

const generateLayout = (visuals, rows, isViewMode, COLUMN_COUNT) => {
  const newPlaceholders = [];
  const newCards = (visuals ?? [])
    .map((visual) => ({
      i: `${visual.VisualId ? visual.VisualId : `card-${visual.PositionX}-${visual.PositionY}`}`,
      x: visual.PositionX ?? 0,
      y: visual.PositionY ?? 0,
      w: visual.Width ?? 1,
      h: visual.Height ?? 1,
      static: false,
      isDraggable: !isViewMode,
      isResizable: !isViewMode,
      updatedOn: new Date(),
    }))
    .sort((a, b) => (a.x > b.x ? -1 : 1));

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < COLUMN_COUNT; x++) {
      newPlaceholders.push({
        i: `ph-${x}-${y}`,
        x,
        y,
        w: 1,
        h: 1,
        static: true,
        isDraggable: false,
        isResizable: false,
        resizeHandles: [],
        className: "cell-placeholder",
      });
    }
  }

  return [newCards, newPlaceholders];
};
